import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Lock, CheckCircle2, AlertTriangle, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/format";
import "@/App.css";

export default function SetupPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null); // { email, full_name, is_setup }
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setInvalid(true); setChecking(false); return; }
      try {
        const { data } = await api.get(`/auth/invite-info/${token}`);
        if (active) setInfo(data);
      } catch {
        if (active) setInvalid(true);
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const isSetup = info?.is_setup;
  const headline = isSetup ? "Set up your account" : "Reset your password";
  const cta = isSetup ? "Activate account" : "Update password";

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/setup-account", { token, password });
      setDone(true);
      toast.success("Account activated. You can now sign in.");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This link is invalid or has expired");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-bg min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md mmm-fade-up">
        <div className="flex flex-col items-center mb-6 text-center">
          <img src={LOGO_URL} alt="MovingMyMama — Your Trusted Resource for Senior Transitions" className="h-20 w-auto object-contain mb-3" />
          <h1 className="font-display text-2xl font-bold text-[#1C4358]">MovingMyMama</h1>
          <p className="text-sm text-[#4B5563]">Command Center · Senior Transitions CRM</p>
        </div>

        <Card className="p-6 sm:p-7 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-md)" }} data-testid="setup-card">
          {checking ? (
            <div className="py-10 flex items-center justify-center" data-testid="setup-loading">
              <Loader2 className="animate-spin text-[#1C4358]" size={26} />
            </div>
          ) : invalid ? (
            <div className="text-center py-4" data-testid="setup-invalid">
              <div className="mx-auto mb-3 h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(156,30,34,0.10)" }}>
                <AlertTriangle size={24} className="text-[#9C1E22]" />
              </div>
              <h2 className="font-display text-lg font-semibold text-[#252221] mb-1">Link not valid</h2>
              <p className="text-sm text-muted-foreground">This invitation or reset link is invalid or has already been used. Please ask your administrator to resend an invite.</p>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="setup-success">
              <CheckCircle2 size={42} className="mx-auto mb-3 text-[#2F7D5A]" />
              <h2 className="font-display text-lg font-semibold text-[#252221] mb-1">You're all set</h2>
              <p className="text-sm text-muted-foreground">Your account is active. Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={18} className="text-[#1C4358]" />
                <h2 className="font-display text-lg font-semibold text-[#252221]">{headline}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                {isSetup ? "Welcome" : "Account"}{info?.full_name ? `, ${info.full_name}` : ""}.
              </p>
              {info?.email && (
                <div className="mb-4 inline-flex text-[12px] px-2.5 py-1 rounded-full" style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }} data-testid="setup-email">
                  {info.email}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="mb-1.5 block text-[13px] font-medium">New password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                      placeholder="At least 8 characters" className="pl-9 h-11" data-testid="setup-password-input" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block text-[13px] font-medium">Confirm password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                      placeholder="Re-enter password" className="pl-9 h-11" data-testid="setup-confirm-input" />
                  </div>
                </div>
                <Button type="submit" disabled={submitting} data-testid="setup-submit-button"
                  className="w-full h-11 text-white text-base" style={{ backgroundColor: "var(--mmm-red)" }}>
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : cta}
                </Button>
              </form>
            </>
          )}
          <div className="text-center mt-5">
            <Link to="/login" className="inline-flex items-center text-sm font-medium text-[#1C4358] hover:underline" data-testid="setup-back-to-login">
              <ArrowLeft size={15} className="mr-1" /> Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
